<template>
  <div class="project-detail">
    <div class="container">
      <div class="project-header">
        <h1 class="project-title">{{ projectDetail.projectname }}</h1>
        <div class="project-meta">
          <span class="project-date">{{ projectDetail.createTime }}</span>
        </div>
      </div>
      <div class="project-content">
        <div class="project-image">
          <img :src="projectDetail.projectimgString" :alt="projectDetail.projectname">
        </div>
        <div class="project-description" v-html="markedContent"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked';
import seoConfig from '@/config/seo';

export default {
  name: 'ProjectDetail',
  metaInfo() {
    return {
      title: `${this.projectDetail.projectname || '項目詳情'} - 新領海機械行有限公司`,
      meta: [
        { name: 'description', content: this.projectDetail.remark ? this.getMetaDescription(this.projectDetail.remark) : '新領海機械行有限公司項目詳情，展示我們的專業工程機械服務案例。' },
        { name: 'keywords', content: `新領海機械行,${this.projectDetail.projectname || '項目詳情'},工程項目,機械應用,成功案例,香港機械公司` },
        { property: 'og:title', content: `${this.projectDetail.projectname || '項目詳情'} - 新領海機械行有限公司` },
        { property: 'og:description', content: this.projectDetail.remark ? this.getMetaDescription(this.projectDetail.remark) : '新領海機械行有限公司項目詳情，展示我們的專業工程機械服務案例。' },
        { property: 'og:type', content: 'article' },
        { property: 'og:url', content: `${seoConfig.domain}/project/detail/${encodeURIComponent(JSON.stringify(this.projectDetail))}` },
        { property: 'og:image', content: this.projectDetail.projectimgString || seoConfig.domain + '/static/img/logo_black.png' }
      ],
      script: [
        {
          type: 'application/ld+json',
          json: {
            '@context': 'https://schema.org',
            '@type': 'Article',
            'headline': this.projectDetail.projectname || '項目詳情',
            'description': this.projectDetail.remark ? this.getMetaDescription(this.projectDetail.remark) : '新領海機械行有限公司項目詳情',
            'datePublished': this.projectDetail.createTime || new Date().toISOString(),
            'publisher': {
              '@type': 'Organization',
              'name': '新領海機械行有限公司',
              'logo': {
                '@type': 'ImageObject',
                'url': seoConfig.domain + '/static/img/logo_black.png'
              }
            },
            'mainEntityOfPage': {
              '@type': 'WebPage',
              '@id': `${seoConfig.domain}/project/detail/${encodeURIComponent(JSON.stringify(this.projectDetail))}`
            },
            'image': this.projectDetail.projectimgString || seoConfig.domain + '/static/img/logo_black.png'
          }
        }
      ]
    }
  },
  data() {
    return {
      projectDetail: {
        projectname: '',
        projectimgString: '',
        remark: '',
        createTime: ''
      }
    };
  },
  created() {
    this.initProjectDetail();
    // 配置marked
    marked.setOptions({
      renderer: new marked.Renderer(),
      gfm: true,
      tables: true,
      breaks: true,
      pedantic: false,
      sanitize: false,
      smartLists: true,
      smartypants: false
    });
  },
  computed: {
    markedContent() {
      return marked(this.projectDetail.remark || '');
    }
  },
  methods: {
    initProjectDetail() {
      const project = this.$route.params.project;
      if (project) {
        this.projectDetail = JSON.parse((project));
      }
    },
    getMetaDescription(content) {
      // Strip HTML tags, markdown, and truncate to ~155 characters for meta description
      const plainText = content.replace(/<[^>]*>?/gm, '').replace(/[#*_\[\]()]/g, '');
      return plainText.length > 155 ? plainText.substring(0, 152) + '...' : plainText;
    }
  }
};
</script>

<style scoped>
.project-detail {
  padding: 40px 0;
  background-color: #fff;
}

.project-header {
  text-align: center;
  margin-bottom: 40px;
}

.project-title {
  font-size: 28px;
  color: #333;
  margin-bottom: 20px;
  font-weight: 600;
}

.project-meta {
  color: #475569;
  font-size: 14px;
}

.project-content {
  max-width: 800px;
  margin: 0 auto;
}

.project-image {
  margin-bottom: 30px;
  text-align: center;
}

.project-image img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.project-description {
  line-height: 1.8;
  color: #333;
  font-size: 16px;
}

.project-description >>> p {
  margin-bottom: 20px;
}

.project-description >>> h1,
.project-description >>> h2,
.project-description >>> h3,
.project-description >>> h4,
.project-description >>> h5,
.project-description >>> h6 {
  margin: 20px 0 15px;
  color: #333;
  font-weight: 600;
}

.project-description >>> h1 {
  font-size: 24px;
}

.project-description >>> h2 {
  font-size: 22px;
}

.project-description >>> h3 {
  font-size: 20px;
}

.project-description >>> h4 {
  font-size: 18px;
}

.project-description >>> h5 {
  font-size: 16px;
}

.project-description >>> h6 {
  font-size: 14px;
}

.project-description >>> img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 20px 0;
}

.project-description >>> ul,
.project-description >>> ol {
  margin: 15px 0;
  padding-left: 30px;
}

.project-description >>> li {
  margin-bottom: 8px;
}

.project-description >>> blockquote {
  border-left: 4px solid #1e73be;
  padding: 10px 20px;
  margin: 20px 0;
  background-color: rgba(30, 115, 190, 0.05);
}

.project-description >>> code {
  background-color: #f5f5f5;
  padding: 2px 4px;
  border-radius: 4px;
  font-family: monospace;
}

.project-description >>> pre {
  background-color: #f5f5f5;
  padding: 15px;
  border-radius: 8px;
  overflow-x: auto;
  margin: 20px 0;
}

.project-description >>> table {
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
}

.project-description >>> th,
.project-description >>> td {
  border: 1px solid #ddd;
  padding: 8px 12px;
  text-align: left;
}

.project-description >>> th {
  background-color: #f5f5f5;
}

@media screen and (max-width: 768px) {
  .project-detail {
    padding: 20px 0;
  }

  .project-title {
    font-size: 24px;
  }

  .project-content {
    padding: 0 15px;
  }

  .project-description {
    font-size: 15px;
  }

  .project-description >>> h1 {
    font-size: 22px;
  }

  .project-description >>> h2 {
    font-size: 20px;
  }

  .project-description >>> h3 {
    font-size: 18px;
  }

  .project-description >>> h4 {
    font-size: 16px;
  }

  .project-description >>> h5 {
    font-size: 15px;
  }

  .project-description >>> h6 {
    font-size: 14px;
  }
}
</style>



// WEBPACK FOOTER //
// src/view/ProjectDetail.vue